import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useMemo } from 'react';
import { useAuthenticate, authClient } from '../lib/auth';
import { customersApi, authApi, tenantsApi } from '../lib/api';
import { NeonUser } from '@lipstick/shared';
import { gtmAuthEvent } from '../lib/gtm';

// Auth context type
export interface Tenant {
    id: string;
    name: string;
    slug: string;
    created_at: string;
    role?: string;
    is_owner?: boolean;
}

interface AuthContextType {
    user: NeonUser | null;
    tenants: Tenant[];
    roles: string[];
    isAdmin: boolean;
    isClient: boolean;
    isLoading: boolean;
    initialized: boolean;
    signIn: (email: string, password: string, callbackURL?: string) => Promise<void>;
    signOut: () => void;
    getToken: () => Promise<string | null>;
    refreshTenants: () => Promise<void>;
    refreshRoles: () => Promise<void>;
    hasRole: (roleOrRoles: string | string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    // Neon Auth hook - enabled: false prevents automatic redirect
    const { data: sessionData, isPending } = useAuthenticate({ enabled: false });
    const rawUser = sessionData?.user;
    const session = sessionData?.session;

    const [tenants, setTenants] = useState<Tenant[]>([]);
    const [roles, setRoles] = useState<string[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [profileSynced, setProfileSynced] = useState(false);

    // Memoize user to prevent effect re-runs on every render
    const user: NeonUser | null = useMemo(() => {
        if (!rawUser) return null;
        return {
            id: rawUser.id,
            email: rawUser.email,
            name: rawUser.name,
            image: rawUser.image,
            emailVerified: rawUser.emailVerified,
            createdAt: rawUser.createdAt,
            updatedAt: rawUser.updatedAt,
            // Compatibility aliases
            primaryEmail: rawUser.email,
            displayName: rawUser.name,
            fullName: rawUser.name,
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawUser?.id, rawUser?.email, rawUser?.name, rawUser?.image, rawUser?.emailVerified, rawUser?.createdAt, rawUser?.updatedAt]);

    // Get access token from active/fresh session only.
    const getToken = useCallback(async (): Promise<string | null> => {
        // Try session data first
        if (session?.token) {
            return session.token;
        }

        // Last resort: try to fetch fresh session
        try {
            const { data } = await authClient.getSession();
            return data?.session?.token || null;
        } catch {
            return null;
        }
    }, [session?.token]);

    const refreshTenants = useCallback(async () => {
        if (!rawUser) return;
        try {
            const token = await getToken();
            if (token) {
                const myTenants: any = await tenantsApi.getMyTenants(async () => token);
                if (Array.isArray(myTenants)) {
                    setTenants(myTenants);
                }
            }
        } catch (error) {
            console.error("Failed to fetch tenants", error);
        }
    }, [rawUser, getToken]);

    const refreshRoles = useCallback(async () => {
        if (!rawUser) return;
        try {
            const token = await getToken();
            if (token) {
                const response = await authApi.getUserRoles(async () => token);
                if (response.roles) {
                    setRoles(response.roles.map((r: any) => r.id));
                }
            }
        } catch (error) {
            console.error("Failed to refresh roles", error);
        }
    }, [rawUser, getToken]);

    // Sync profile and fetch initial data
    useEffect(() => {
        let mounted = true;

        async function initAuth() {
            if (!rawUser) {
                if (!isPending) {
                    if (mounted) setIsLoading(false);
                }
                return;
            }

            try {
                const token = await getToken();
                if (!token) {
                    if (mounted) setIsLoading(false);
                    return;
                }

                // 1. Fetch Roles
                try {
                    const response = await authApi.getUserRoles(async () => token);
                    if (response.roles && mounted) {
                        setRoles(response.roles.map((r: any) => r.id));
                    }
                } catch (e) {
                    console.error("Role fetch failed", e);
                }

                // 2. Fetch Tenants
                try {
                    const myTenants: any = await tenantsApi.getMyTenants(async () => token);
                    if (Array.isArray(myTenants) && mounted) {
                        setTenants(myTenants);
                    }
                } catch (e) {
                    console.error("Tenant fetch failed", e);
                }

                // 3. Sync Profile (if needed)
                if (!profileSynced) {
                    try {
                        await customersApi.syncCustomer(
                            { email: rawUser.email || '', full_name: rawUser.name || undefined },
                            async () => token
                        );
                        await authApi.syncUser(async () => token);
                        if (mounted) setProfileSynced(true);
                    } catch (e) {
                        console.error("Profile sync failed", e);
                    }
                }
            } catch (error) {
                console.error('Auth initialization error:', error);
            } finally {
                if (mounted) setIsLoading(false);
            }
        }

        initAuth();

        return () => { mounted = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rawUser?.id, isPending, profileSynced, getToken]);

    // Sign out handler
    const signOut = useCallback(async () => {
        gtmAuthEvent('sign_out');
        await authClient.signOut();
        setRoles([]);
        setTenants([]);
        setProfileSynced(false);
    }, []);

    const value: AuthContextType = {
        user,
        tenants,
        roles,
        isAdmin: roles.includes('super_admin') || roles.includes('admin'),
        isClient: roles.includes('client'),
        isLoading,
        initialized: !isLoading,
        signIn: async (email: string, password: string, callbackURL = '/onboarding') => {
            await authClient.signIn.email({
                email,
                password,
                callbackURL,
            });
        },
        signOut,
        getToken,
        refreshTenants,
        refreshRoles,
        hasRole: (roleOrRoles: string | string[]) => {
            const required = Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles];
            return roles.some(r => required.includes(r));
        }
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
